import { AlertType, SkatingPass } from '@types';
import { AlertModal, Card, PageHeader } from '_atoms';
import { AdminService } from '_services';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdCardMembership,
  MdClose,
  MdEdit,
  MdExpandLess,
  MdExpandMore,
  MdSave,
} from 'react-icons/md';

const SkatingPasses: React.FC = () => {
  const [seasonPasses, setSeasonPasses] = useState<SkatingPass[]>([]);
  const [expandedPasses, setExpandedPasses] = useState<Set<string>>(new Set());
  const [editingPasses, setEditingPasses] = useState<Set<string>>(new Set());
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handlePassChange = (
    passId: string,
    field: keyof SkatingPass,
    value: string | number | boolean,
  ) => {
    setSeasonPasses(prev =>
      prev.map(pass =>
        pass.id === passId ? { ...pass, [field]: value } : pass,
      ),
    );
  };

  const handleEditToggle = (passId: string) => {
    setEditingPasses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(passId)) {
        newSet.delete(passId);
      } else {
        newSet.add(passId);
        // Always expand when entering edit mode
        setExpandedPasses(prevExpanded => {
          const newExpandedSet = new Set(prevExpanded);
          newExpandedSet.add(passId);
          return newExpandedSet;
        });
      }
      return newSet;
    });
  };

  const handleExpandToggle = (passId: string) => {
    setExpandedPasses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(passId)) {
        newSet.delete(passId);
      } else {
        newSet.add(passId);
      }
      return newSet;
    });
  };

  const handleSavePass = useCallback(
    async (pass: SkatingPass) => {
      setIsLoadingSave(true);

      try {
        await AdminService.updateSeasonSkatingPass(pass);
        showAlert('success', 'Success', 'Season pass updated successfully!');
        setEditingPasses(prev => {
          const newSet = new Set(prev);
          newSet.delete(pass.id);
          return newSet;
        });
      } catch (error) {
        console.error('Error updating season pass:', error);
        showAlert(
          'error',
          'Error',
          'Error updating season pass. Please try again.',
        );
      } finally {
        setIsLoadingSave(false);
      }
    },
    [showAlert],
  );

  // Load season passes on page load
  useEffect(() => {
    const loadSeasonPasses = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await AdminService.getSeasonSkatingPasses()) as unknown as SkatingPass[];

        setSeasonPasses(responseData);
      } catch (error) {
        console.error('Error loading season passes:', error);
        showAlert('error', 'Error', 'Failed to load season passes.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadSeasonPasses();
  }, [showAlert]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdCardMembership}
        title="Season Passes Management"
        description="Configure season passes, pricing, and availability settings."
      />

      {/* Main Content */}
      {isLoadingInitial ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading season passes...</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {seasonPasses.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <p className="text-gray-600">No season passes found.</p>
              </div>
            </Card>
          ) : (
            seasonPasses.map(pass => {
              const isExpanded = expandedPasses.has(pass.id);
              const isEditing = editingPasses.has(pass.id);

              return (
                <Card key={pass.id} className="flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleExpandToggle(pass.id)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        {isExpanded ? (
                          <MdExpandLess className="w-5 h-5 text-gray-600" />
                        ) : (
                          <MdExpandMore className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                      {pass.photoUrl && (
                        <div className="flex-shrink-0">
                          <img
                            src={pass.photoUrl}
                            alt={pass.name}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {pass.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {pass.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-xs text-gray-500">
                            Adult: ${pass.adultPrice}
                          </span>
                          <span className="text-xs text-gray-500">
                            Child: ${pass.childPrice}
                          </span>
                          <span className="text-xs text-gray-500">
                            Max Uses:{' '}
                            {pass.maxUses === null || pass.maxUses === 0
                              ? 'Unlimited'
                              : `${pass.maxUses} sessions`}
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              pass.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {pass.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditToggle(pass.id)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        {isEditing ? (
                          <MdClose className="mr-1" />
                        ) : (
                          <MdEdit className="mr-1" />
                        )}
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 space-y-4">
                      {isEditing ? (
                        // Edit view
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Name
                            </label>
                            <input
                              type="text"
                              value={pass.name}
                              onChange={e =>
                                handlePassChange(
                                  pass.id,
                                  'name',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <textarea
                              value={pass.description}
                              onChange={e =>
                                handlePassChange(
                                  pass.id,
                                  'description',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] resize-y"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Disclaimer
                            </label>
                            <textarea
                              value={pass.disclaimer}
                              onChange={e =>
                                handlePassChange(
                                  pass.id,
                                  'disclaimer',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[80px] resize-y"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Adult Price ($)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pass.adultPrice}
                                onChange={e =>
                                  handlePassChange(
                                    pass.id,
                                    'adultPrice',
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Child Price ($)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={pass.childPrice}
                                onChange={e =>
                                  handlePassChange(
                                    pass.id,
                                    'childPrice',
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                              </label>
                              <div className="flex items-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handlePassChange(
                                      pass.id,
                                      'isActive',
                                      !pass.isActive,
                                    )
                                  }
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                    pass.isActive
                                      ? 'bg-blue-600'
                                      : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      pass.isActive
                                        ? 'translate-x-6'
                                        : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                                <span className="ml-3 text-sm text-gray-700">
                                  {pass.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Save Button */}
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleSavePass(pass)}
                              disabled={
                                isLoadingSave ||
                                !pass.name.trim() ||
                                !pass.description.trim() ||
                                pass.adultPrice < 0 ||
                                pass.childPrice < 0
                              }
                              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                              <MdSave className="mr-2" />
                              {isLoadingSave ? 'Saving...' : 'Save Pass'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Read-only view
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <p className="text-sm text-gray-600">
                              {pass.description}
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Disclaimer
                            </label>
                            <p className="text-sm text-gray-600">
                              {pass.disclaimer}
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Adult Price
                              </label>
                              <p className="text-sm text-gray-600">
                                ${pass.adultPrice}
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Child Price
                              </label>
                              <p className="text-sm text-gray-600">
                                ${pass.childPrice}
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Max Uses
                              </label>
                              <p className="text-sm text-gray-600">
                                {pass.maxUses}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default SkatingPasses;
